import {
  BookingRecord,
  getStoredBookings,
  saveBookingToStorage,
  updateStoredBooking,
  deleteStoredBooking
} from '../utils/excelExport';
import { StaffPartner } from './partnerService';

/**
 * Persists a new booking order:
 * 1. Synchronously saves to localStorage (instant client feedback & offline guarantee)
 * 2. Asynchronously saves to Cloudflare D1 via /api/bookings
 */
export async function createBookingOrder(
  booking: BookingRecord
): Promise<{
  success: boolean;
  remoteSaved: boolean;
  bookingRef: string;
  startOtp: string;
  assignedPartner?: { id: string; name: string; phone: string; specialty: string } | null;
  error?: string;
  slotFull?: boolean;
}> {
  // Ensure Start OTP is generated (4-digit security code)
  if (!booking.startOtp) {
    booking.startOtp = Math.floor(1000 + Math.random() * 9000).toString();
  }

  // Ensure Pay After Service defaults
  booking.paymentMethod = 'Pay After Service';
  if (!booking.paymentStatus) {
    booking.paymentStatus = 'Pending Collection';
  }
  if (!booking.serviceStatus) {
    booking.serviceStatus = 'confirmed';
  }

  try {
    const res = await fetch('/api/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(booking)
    });

    if (res.status === 409) {
      // Slot was fully booked!
      const errData = (await res.json().catch(() => ({}))) as any;
      return {
        success: false,
        remoteSaved: false,
        bookingRef: booking.bookingRef,
        startOtp: '',
        error: errData?.error || 'This time slot is already fully booked. Please choose another time slot.',
        slotFull: true
      };
    }

    if (res.ok) {
      const data = (await res.json()) as any;
      if (data.assignedPartner) {
        booking.partnerId = data.assignedPartner.id;
        booking.partnerName = data.assignedPartner.name;
        booking.partnerPhone = data.assignedPartner.phone;
        booking.servicePerson = `${data.assignedPartner.name} (${data.assignedPartner.specialty || 'Beauty Specialist'})`;
        booking.serviceStatus = 'assigned';
      }

      // Save updated record to localStorage
      saveBookingToStorage(booking);

      return {
        success: true,
        remoteSaved: true,
        bookingRef: booking.bookingRef,
        startOtp: booking.startOtp,
        assignedPartner: data.assignedPartner || null
      };
    } else {
      console.warn('Cloudflare D1 sync responded with non-200:', res.status);
      saveBookingToStorage(booking);
      return {
        success: true,
        remoteSaved: false,
        bookingRef: booking.bookingRef,
        startOtp: booking.startOtp
      };
    }
  } catch (err) {
    console.warn('Offline or local preview mode: Cloudflare D1 endpoint not reached:', err);
    saveBookingToStorage(booking);
    return {
      success: true,
      remoteSaved: false,
      bookingRef: booking.bookingRef,
      startOtp: booking.startOtp
    };
  }
}

/**
 * Retrieves bookings for a customer by phone number:
 * 1. Reads local bookings
 * 2. Fetches remote bookings from Cloudflare D1
 * 3. Merges and deduplicates by bookingRef
 */
export async function getCustomerBookings(phone: string): Promise<BookingRecord[]> {
  const cleanTarget = phone.replace(/\D/g, '').slice(-10);

  // 1. Local bookings matching phone
  const localAll = getStoredBookings();
  const localMatched = localAll.filter((b) => {
    const p = (b.phone || '').replace(/\D/g, '').slice(-10);
    return p === cleanTarget;
  });

  try {
    const res = await fetch(`/api/bookings?phone=${cleanTarget}`, {
      headers: { 'Cache-Control': 'no-store' }
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; bookings: BookingRecord[] };
      if (data && Array.isArray(data.bookings)) {
        // Merge: remote bookings take precedence for status / staff assignments
        const map = new Map<string, BookingRecord>();

        // Add local first
        localMatched.forEach((b) => map.set(b.bookingRef, b));

        // Override with remote
        data.bookings.forEach((b) => map.set(b.bookingRef, b));

        return Array.from(map.values()).sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      }
    }
  } catch (err) {
    console.warn('Could not fetch from Cloudflare D1, returning local bookings:', err);
  }

  return localMatched;
}

/**
 * Customer submits rating & feedback review for completed booking
 */
export async function submitCustomerRating(
  bookingRef: string,
  rating: number,
  reviewText: string
): Promise<BookingRecord | null> {
  const updated = updateStoredBooking(bookingRef, {
    customerRating: rating,
    customerReview: reviewText.trim()
  });

  try {
    await fetch('/api/bookings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bookingRef,
        customerRating: rating,
        customerReview: reviewText.trim()
      })
    });
  } catch (err) {
    console.warn('Could not sync customer rating to D1:', err);
  }

  return updated;
}

/**
 * Customer cancels a booking
 */
export async function cancelCustomerBooking(
  bookingRef: string,
  reason?: string
): Promise<BookingRecord | null> {
  const updated = updateStoredBooking(bookingRef, {
    serviceStatus: 'cancelled',
    status: 'Cancelled',
    specialRequests: reason ? `[Cancelled]: ${reason}` : 'Cancelled by customer'
  });

  try {
    await fetch('/api/bookings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bookingRef,
        serviceStatus: 'cancelled',
        status: 'Cancelled',
        specialRequests: reason ? `[Cancelled]: ${reason}` : 'Cancelled by customer'
      })
    });
  } catch (err) {
    console.warn('Could not sync booking cancellation to D1:', err);
  }

  return updated;
}

/**
 * Reschedules an appointment date & time slot:
 * - Updates localStorage booking record
 * - Syncs to Cloudflare D1 via /api/bookings (customer) or /api/admin/bookings (admin)
 */
export async function rescheduleBooking(
  bookingRef: string,
  newDate: string,
  newSlot: string,
  options?: {
    adminPin?: string;
    reason?: string;
  }
): Promise<{ success: boolean; updatedBooking: BookingRecord | null; error?: string }> {
  const existing = getStoredBookings().find((b) => b.bookingRef === bookingRef);
  const note = options?.reason ? `[Rescheduled to ${newDate} (${newSlot})]: ${options.reason}` : undefined;

  const updated = updateStoredBooking(bookingRef, {
    preferredDate: newDate,
    preferredSlot: newSlot,
    specialRequests: note 
      ? (existing?.specialRequests ? `${existing.specialRequests} | ${note}` : note)
      : existing?.specialRequests
  });

  try {
    const isSuperAdmin = !!options?.adminPin;
    const endpoint = isSuperAdmin ? '/api/admin/bookings' : '/api/bookings';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (isSuperAdmin) {
      headers['x-admin-pin'] = options.adminPin!;
    }

    const payload: any = {
      bookingRef,
      preferredDate: newDate,
      preferredSlot: newSlot
    };
    if (note) {
      payload.specialRequests = updated?.specialRequests || note;
    }

    const res = await fetch(endpoint, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(payload)
    });

    if (res.ok) {
      return { success: true, updatedBooking: updated };
    } else {
      console.warn('Reschedule remote sync returned non-200:', res.status);
    }
  } catch (err: any) {
    console.warn('Could not sync reschedule to remote D1:', err);
  }

  return { success: true, updatedBooking: updated };
}

/**
 * Retrieves all bookings for Admin:
 * Requires secret admin PIN
 */
export async function getAdminBookings(
  pin: string
): Promise<{ bookings: BookingRecord[]; isD1Connected: boolean }> {
  const localAll = getStoredBookings();
  const map = new Map<string, BookingRecord>();
  localAll.forEach((b) => map.set(b.bookingRef, b));

  try {
    const res = await fetch('/api/admin/bookings', {
      headers: {
        'x-admin-pin': pin,
        'Cache-Control': 'no-store'
      }
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; bookings: BookingRecord[] };
      if (data && Array.isArray(data.bookings)) {
        // Overlay remote bookings
        data.bookings.forEach((b) => map.set(b.bookingRef, b));

        return {
          bookings: Array.from(map.values()).sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          ),
          isD1Connected: true
        };
      }
    }
  } catch (err) {
    console.warn('Could not fetch admin bookings from Cloudflare D1:', err);
  }

  return {
    bookings: Array.from(map.values()),
    isD1Connected: false
  };
}

/**
 * Admin updates booking status or assigned service person
 */
export async function updateAdminBooking(
  pin: string,
  bookingRef: string,
  updates: Partial<BookingRecord>
): Promise<boolean> {
  // Update local
  updateStoredBooking(bookingRef, updates);

  // Update remote D1
  try {
    const res = await fetch('/api/admin/bookings', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify({ bookingRef, ...updates })
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Admin assigns a beautician to a booking
 */
export async function assignStaffToBooking(
  pin: string,
  bookingRef: string,
  staff: StaffPartner
): Promise<boolean> {
  const updates: Partial<BookingRecord> = {
    partnerId: staff.id,
    partnerName: staff.name,
    partnerPhone: staff.phone,
    servicePerson: `${staff.name} (${staff.specialty})`,
    serviceStatus: 'assigned',
    status: 'Assigned'
  };

  return updateAdminBooking(pin, bookingRef, updates);
}

/**
 * Admin deletes a booking
 */
export async function deleteAdminBooking(pin: string, bookingRef: string): Promise<boolean> {
  deleteStoredBooking(bookingRef);

  try {
    const res = await fetch(`/api/admin/bookings?ref=${encodeURIComponent(bookingRef)}`, {
      method: 'DELETE',
      headers: {
        'x-admin-pin': pin
      }
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Admin manually creates an appointment (from Phone Call, Instagram, WhatsApp, etc.):
 * 1. Synchronously saves to localStorage
 * 2. Asynchronously syncs to Cloudflare D1 via /api/admin/bookings
 */
export async function createAdminManualBooking(
  pin: string,
  booking: BookingRecord
): Promise<{ success: boolean; remoteSaved: boolean; bookingRef: string; startOtp: string }> {
  if (!booking.startOtp) {
    booking.startOtp = Math.floor(1000 + Math.random() * 9000).toString();
  }
  if (!booking.bookingRef) {
    booking.bookingRef = 'BLISHKA-M' + Math.floor(100000 + Math.random() * 900000);
  }

  booking.paymentMethod = booking.paymentMethod || 'Pay After Service';
  booking.paymentStatus = booking.paymentStatus || 'Pending Collection';
  booking.status = booking.status || (booking.partnerId ? 'Assigned' : 'Confirmed');
  booking.serviceStatus = booking.serviceStatus || (booking.partnerId ? 'assigned' : 'confirmed');

  // Save to localStorage immediately
  saveBookingToStorage(booking);

  try {
    const res = await fetch('/api/admin/bookings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pin': pin
      },
      body: JSON.stringify(booking)
    });

    if (res.ok) {
      await res.json();
      return {
        success: true,
        remoteSaved: true,
        bookingRef: booking.bookingRef,
        startOtp: booking.startOtp
      };
    }
  } catch (err) {
    console.warn('Could not sync manual booking to D1:', err);
  }

  return {
    success: true,
    remoteSaved: false,
    bookingRef: booking.bookingRef,
    startOtp: booking.startOtp
  };
}
